import Phaser from 'phaser';
import type { Zone, TouchSnapshot } from './InputController';
import { tokens } from '../design/tokens';
import { palette } from '../design/palette';

type Direction = -1 | 0 | 1;

interface ZoneVisual {
  arrow: Phaser.GameObjects.Triangle;
  /** 0 = fully hidden, 1 = fully shown. Animated toward target. */
  visibility: number;
  /** 1 when the thumb is actively scrolling in some direction, 0 otherwise. */
  targetVisibility: number;
  /** Current arrow orientation. Holds last direction while fading out. */
  direction: Direction;
}

const ZONES: Zone[] = ['left', 'right'];

/**
 * Per-touch direction arrow drawn near each thumb. Shown only while the
 * thumb is past the joystick deadzone — points up when scrolling up, down
 * when scrolling down. Hidden in the deadzone and when no touch is active.
 */
export class JoystickOverlay {
  private scene: Phaser.Scene;
  private visuals: Record<Zone, ZoneVisual>;

  constructor(scene: Phaser.Scene, depth: number = 100) {
    this.scene = scene;
    this.visuals = {
      left: this.makeZoneVisual(depth),
      right: this.makeZoneVisual(depth),
    };
  }

  /**
   * Drive the overlay from the current snapshot list. Snapshots use logical
   * px (same coord system as the camera).
   */
  update(snapshots: TouchSnapshot[], dtMs: number): void {
    const byZone: Partial<Record<Zone, TouchSnapshot>> = {};
    for (const s of snapshots) byZone[s.zone] = s;

    const gui = tokens.input.joystickGui;
    const dead = tokens.input.joystick.deadzonePx;
    const fadeStep = gui.fadeMs <= 0 ? 1 : dtMs / gui.fadeMs;

    for (const zone of ZONES) {
      const vis = this.visuals[zone];
      const snap = byZone[zone];

      let dir: Direction = 0;
      if (snap) {
        const offset = snap.lastY - snap.centerY;
        if (offset > dead) dir = 1;
        else if (offset < -dead) dir = -1;
      }

      vis.targetVisibility = dir === 0 ? 0 : 1;

      if (dir !== 0 && dir !== vis.direction) {
        vis.direction = dir;
        // Base triangle points up; flip vertically when scrolling down.
        vis.arrow.setScale(1, -dir);
      }

      if (vis.visibility < vis.targetVisibility) {
        vis.visibility = Math.min(1, vis.visibility + fadeStep);
      } else if (vis.visibility > vis.targetVisibility) {
        vis.visibility = Math.max(0, vis.visibility - fadeStep);
      }

      if (vis.visibility === 0) {
        vis.arrow.setVisible(false);
        continue;
      }

      vis.arrow.setVisible(true);

      if (snap && vis.direction !== 0) {
        const arrowY = snap.lastY + vis.direction * gui.arrowOffsetPx;
        vis.arrow.setPosition(snap.centerX, arrowY);
      }

      vis.arrow.setAlpha(gui.arrowAlpha * vis.visibility);
    }
  }

  destroy(): void {
    for (const zone of ZONES) {
      this.visuals[zone].arrow.destroy();
    }
  }

  private makeZoneVisual(depth: number): ZoneVisual {
    const gui = tokens.input.joystickGui;
    const w = gui.arrowWidthPx;
    const h = gui.arrowHeightPx;
    // Up-pointing triangle centered on its local origin: tip at (0, -h/2),
    // base at y = +h/2. setScale(1, -1) flips to down-pointing.
    const arrow = this.scene.add
      .triangle(0, 0, 0, -h / 2, -w / 2, h / 2, w / 2, h / 2, palette.uiText, 1)
      .setVisible(false)
      .setDepth(depth + 1);

    return {
      arrow,
      visibility: 0,
      targetVisibility: 0,
      direction: 0,
    };
  }
}
